import { Component, ViewEncapsulation, OnInit, inject } from '@angular/core';

import { BookJob } from 'src/providers/book-job/book-job';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Util } from 'src/shared/util/util';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { Loading } from 'src/shared/components/loading/loading';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { TidySelectNumberValueModel, TidySelectStringValueModel } from 'src/models/tidy-select-item.model';

@Component({
    templateUrl: 'list-settings.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ToDosListSettingsPage implements OnInit {

  addressItems: TidySelectNumberValueModel[];
  addressToDoListItems: any;
  allToDoListItems: any;
  errorMessage: string;
  form: UntypedFormGroup;
  didUpdateDefaultSetting: boolean;
  hasNoDefaultList: boolean;
  isRentalClient: boolean;
  isLoadingLists: boolean;
  isRightSideContent: boolean;
  loaded: boolean;
  services: any;
  serviceItems: TidySelectStringValueModel[];
  selectedService: any;
  selectedAddress: any;
  boundGoToToDosPage = this.goToToDosPage.bind(this);

  private readonly toDosQueries = inject(ToDosQueriesService);

constructor(
    private bookJob: BookJob,
    private client: Client,
    private currentAddress: CurrentAddress,
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public toDos: ToDos,
    private util: Util
  ) {
    this.form = this.fb.group({
      address: [''],
      addressServiceDefaultList: [''],
      addressDefaultList: [''],
      accountServiceDefaultList: [''],
      accountDefaultList: [''],
    });
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      const addressResponse = await this.client.getAddresses();
      const addressId = this.client.getSelectedAddressId(addressResponse);
      await this.getAddressItems(addressId);
      await this.getServiceItems();
      this.allToDoListItems = await this.toDos.getToDoListItemsForAddress(this.selectedAddress.value, new TranslationPipe().transform('No Default'));
      this.patchForms();
      this.loaded = true;
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  async getAddressItems(addressId) {
    const addresses = await this.client.getAddresses();
    this.addressItems = this.client.parseAddressList(addresses, true);
    this.selectedAddress = this.addressItems.find(address => address.value === addressId);
    this.addressItems.pop();
  }

  async getServiceItems() {
    this.services = await this.bookJob.fetchAllRequiredTeamServices();
    this.selectedService = this.services[0];
    this.serviceItems = [];
    this.serviceItems = this.services.map((service) => ({
        viewValue: service.name,
        value: service.key
      }));
  }

  patchForms() {
    const addressId = parseFloat(localStorage.getItem('addressId'));
    this.form.patchValue({
      address: addressId,
      addressServiceDefaultList: this.getAddressServiceDefaultList(addressId) || 0,
      addressDefaultList: this.getAddressDefaultList(addressId) || 0,
      accountServiceDefaultList: this.getAccountServiceDefaultList() || 0,
      accountDefaultList: this.getAccountDefaultList() || 0
    });
  }

  getAddressServiceDefaultList(addressId) {
    let defaultList = null;
    this.allToDoListItems.map((list) => {
      list.defaultListSettings.map((setting) => {
        if (setting.key ===`default_list.address.${addressId}.${this.selectedService.key}`) {
          defaultList = list;
        }
      });
    });
    return defaultList?.value;
  }

  getAccountServiceDefaultList() {
    let defaultList = null;
    this.allToDoListItems.map((list) => {
      list.defaultListSettings.map((setting) => {
        if (setting.key ===`default_list.${this.selectedService.key}`) {
          defaultList = list;
        }
      });
    });
    return defaultList?.value;
  }

  getAddressDefaultList(addressId) {
    let defaultList = null;
    this.allToDoListItems.map((list) => {
      list.defaultListSettings.map((setting) => {
        if (setting.key ===`default_list.address.${addressId}`) {
          defaultList = list;
        }
      });
    });
    return defaultList?.value;
  }

  getAccountDefaultList() {
    let defaultList = null;
    this.allToDoListItems.map((list) => {
      list.defaultListSettings.map((setting) => {
        if (setting.key ==='default_list.account') {
          defaultList = list;
        }
      });
    });
    return defaultList?.value;
  }

  async updateAddressServiceDefaultList(listId) {
    const key = `default_list.address.${this.form.value.address}.${this.selectedService.key}`;
    this.updateDefault(listId, key);
  }

  async updateAddressDefaultList(listId) {
    const key = `default_list.address.${this.form.value.address}`;
    this.updateDefault(listId, key);
  }

  async updateAccountServiceDefaultList(listId) {
    const key = `default_list.${this.selectedService.key}`;
    this.updateDefault(listId, key);
  }

  async updateAccountDefaultList(listId) {
    const key = 'default_list.account';
    this.updateDefault(listId, key);
  }

  async updateDefault(listId, defaultListSettingsKey) {
    const isSettingNoDefault = listId === 0;
    if (isSettingNoDefault) {
      listId = this.getCurrentDefaultListId(defaultListSettingsKey);
    }
    const payload = {
      address_task_list_id: listId,
      address_id: this.form.value.address,
      settings: [{
        key: defaultListSettingsKey,
        value: !isSettingNoDefault
      }]
    }
    try {
      await this.toDos.saveDefaultList(listId, payload);
      // Invalidate lists cache for the address since default settings changed
      this.toDosQueries.invalidateLists(this.form.value.address);
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      this.didUpdateDefaultSetting = true;
      this.showSuccessMessage();
    } catch(err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  getCurrentDefaultListId(key) {
    let defaultListId = null;
    this.allToDoListItems.map((item) => {
      item.defaultListSettings.map((setting) => {
        if (setting.key === key) {
          defaultListId = item.value;
        }
      });
    });
    this.addressToDoListItems?.map((item) => {
      item.defaultListSettings.map((setting) => {
        if (setting.key === key) {
          defaultListId = item.value;
        }
      });
    });
    return defaultListId;
  }

  showSuccessMessage() {
    const successMessage = 'Successfully updated checklist settings';
    this.util.showSuccess(successMessage);
  }

  async changeAddress(addressId) {
    this.currentAddress.addressId = addressId.toString();
    try {
      this.isLoadingLists = true;
      this.didUpdateDefaultSetting = false;
      this.selectedAddress = this.addressItems.find((address) => address.value === addressId);
      this.allToDoListItems = await this.toDos.getToDoListItemsForAddress(this.selectedAddress.value, new TranslationPipe().transform('No Default'));
      this.patchForms();
      this.isLoadingLists = false;
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  async changeSelectedService(serviceKey) {
    try {
      this.isLoadingLists = true;
      if (this.didUpdateDefaultSetting) {
        this.allToDoListItems = await this.toDos.getToDoListItemsForAddress(this.selectedAddress.value, new TranslationPipe().transform('No Default'));
      }
      this.didUpdateDefaultSetting = false;
      this.selectedService = this.services.find(service => service.key === serviceKey);
      this.patchForms();
      this.isLoadingLists = false;
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  goToConcierge() {
    const params = {
      title: 'Concierge Action',
      type: 'support.concierge_task',
      isAddingToDoList: true
    }
    this.rightSidePanelService.navigateTo('contact-concierge', params);
  }

  goToToDosPage() {
    this.navCtrl.navigateForward('all-to-dos');
  }

}
